-- | GraphQL queries and types used for the search widget.
module API.Expectations where

import Common

import API.Pagination (Page, unpaged)
import API.Types (CourseID, DocumentID, ID, StudentID, ExpectationID)
import Data.Argonaut (class EncodeJson, class DecodeJson)
import Data.Argonaut as J
import Data.Argonaut.Decode (JsonDecodeError(..))
import Data.Array (concatMap, filter)
import GraphQL.Query (Query, buildQuery)
import GraphQL.Shape (shapeOf, shapeToStringInner)
import GraphQL.Type (class HasQType, QID(..), QType(..), NamedType, namedType)
import Internal.Util (justIf)
import Type.Prelude (Proxy(..))
import Widgets.BetterSelect (SearchAff(..), SelectOption, simpleFrontendSearch, wrapSelectOption)


type ExpectationFilterEntry = { id :: ID }

type ExpCourse =
    { id :: CourseID
    , name :: String
    , label :: String
    }

type GroupCourse =
    { id :: CourseID
    , name :: String
    , label :: String
    , group_label :: String
    }

courseFields :: String
courseFields = shapeToStringInner $ shapeOf (Proxy :: Proxy ExpCourse)

-- | "Downgrade" a course with a group label to an `ExpCourse`
groupCourseToExp :: GroupCourse -> ExpCourse
groupCourseToExp { id, name, label } = { id, name, label }

-- | "Upgrade" an ExpCourse to a GroupCourse
-- |
-- | NOTE: Since the `ExpCourse` type does not have a group label, the result
-- | of this function will have an empty string in that field.
expCourseToGroup :: ExpCourse -> GroupCourse
expCourseToGroup { id, name, label } = { id, name, label, group_label: "" }

-- | Query the backend for data to pupulate the search widget.
courseListQuery :: StudentID -> Query (Array GroupCourse)
courseListQuery student_id = buildQuery "courses" { student_id }

courseListSelAff :: StudentID -> SearchAff GroupCourse
courseListSelAff student = simpleFrontendSearch _.label $ wrapSelectOption _.label $
    courseListQuery student


type Occupation =
    { id :: ID
    , label :: String
    }
type OccupationType = String

-- | Query the backend for data to pupulate the search widget.
occupationListQuery :: OccupationType -> Query (Array Occupation)
occupationListQuery otype' = unpaged $ buildQuery "occupations" { type: [otype], first: 2147483647 }
  where
    otype = (namedType otype' :: NamedType "OccupationType" String)

occupationListSelAff :: OccupationType -> SearchAff Occupation
occupationListSelAff otype = simpleFrontendSearch _.label $ wrapSelectOption _.label $
    occupationListQuery otype


-- | Combined employer and position record for use in the select box in employer expectations tab.
type EmployerPosition = SelectOption { id :: ID }

-- Actual response from GraphQL before processing
type EmployerPosQueryData =
    { id :: ID
    , company :: String
    , positions :: Array
        { id :: ID
        , name :: String
        , expectations :: { data :: Array { id :: ExpectationID } }
        }
    }

employerPosSearchSelAff :: DocumentID -> SearchAff ExpectationFilterEntry
employerPosSearchSelAff docId = BackendSearch \search ->
    employerPosListQuery docId search

employerWhere :: NamedType "QueryEmployersWhereWhereConditions" _
employerWhere = namedType
    { "HAS":
        { relation: "positions"
        , condition:
            { "HAS": { relation: "expectations" } }
        }
    }

employerPosListQuery :: DocumentID -> String -> Query (Array EmployerPosition)
employerPosListQuery form_id search = map processEmployerPos $ unpaged $
    buildQuery "employers" { form_id, search, "where": employerWhere }

employerPosSearchSelAff_ :: SearchAff ExpectationFilterEntry
employerPosSearchSelAff_ = BackendSearch \search ->
    employerPosListQuery_ search

employerPosListQuery_ :: String -> Query (Array EmployerPosition)
employerPosListQuery_ search = map processEmployerPos $ unpaged $
    buildQuery "employers" { search, "where": employerWhere }

processEmployerPos :: Array EmployerPosQueryData -> Array EmployerPosition
processEmployerPos = concatMap processEmployer
  where
    -- Filter out employer positions with no expectations.
    --
    -- While employers with no expectations are filtered out in the GraphQL
    -- query, if an employer has multiple positions (some of which do not
    -- have expectations) those employer - position pairs will still be shown
    -- in the drop-down. This removes them.
    -- @todo: at time of writing, there's no support for filtering positions
    -- in the GraphQL back-end. Maybe request support be added, if this
    -- isn't enough.
    filteredPositions emp = filter (\pos -> length pos.expectations.data > 0) emp.positions

    processEmployer :: EmployerPosQueryData -> Array EmployerPosition
    processEmployer emp = map (\pos ->
        { label: emp.company <> " - " <> pos.name
        , value: { id: pos.id }
        }) (filteredPositions emp)
    

type ExpectationBase r =
    { id :: ExpectationID
    , description :: String
    , category :: Maybe String
    , sub_category :: Maybe String
    | r
    }

type Expectation = ExpectationBase ()

data ExpectationType = Overall | Specific

instance encExType :: EncodeJson ExpectationType where
    encodeJson Overall = J.fromString "OVERALL"
    encodeJson Specific = J.fromString "SPECIFIC"

instance exTyQT :: HasQType ExpectationType where
    gqlType _ = QType "ExpectationType"

instance DecodeJson ExpectationType where
    decodeJson json = case J.toString json of
        Just "OVERALL" -> Right Overall
        Just "SPECIFIC" -> Right Specific
        _ -> Left $ UnexpectedValue json

expectationFields :: String
expectationFields = shapeToStringInner $ shapeOf (Proxy :: Proxy Expectation)

courseExpectationsQuery :: Array ExpectationType -> CourseID -> Query (Page Expectation)
courseExpectationsQuery types id = buildQuery "expectations"
    { course_id: [id], type: justIf (length types > 0) types }

nocExpectationsQuery :: ID -> Query (Page Expectation)
nocExpectationsQuery id = buildQuery "expectations" { occupation_id: [QID id] }

tradeExpectationsQuery :: ID -> Query (Page Expectation)
tradeExpectationsQuery = nocExpectationsQuery

positionExpectationsQuery :: ID -> Query (Page Expectation)
positionExpectationsQuery id = buildQuery "expectations" { position_id: [QID id] }
